import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { DynamicContentEntranceService } from '@services/dynamic-content/dynamic-content-entrance.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GroupService } from '@services/groups/group.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { GroupHasRoleInOrganisationService } from '@services/groups/group-has-role-in-organisation.service';
import { DynamicContentService } from '@services/dynamic-content/dynamic-content.service';
import { TranslateService } from '@services/translate/translate.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';
import { DialogService } from '@services/dialog/dialog.service';
import { HttpClient } from '@angular/common/http';
import { GroupHasSecuredAppService } from '@services/groups/group-has-secured-app.service';
import { TimePickerComponent } from '../../../components/time-picker/time-picker.component';
import { AddItemsFromList } from '../../user-admin/user-admin-edit/dialogs/add-items-from-list/add-items-from-list.component';
import { ContentSelectionComponent } from '../content-selection/content-selection.component';
import { environment } from '../../../../environments/environment';
import { ActiveStateService } from '@services/active-state/active-state.service';

@Component({
  selector: 'notification-form',
  providers: [NotificationsService, GroupService, GroupHasRoleInOrganisationService, DynamicContentService, DynamicContentEntranceService, PackageInstalledAppsService, GroupHasSecuredAppService],
  templateUrl: './notification-form.component.html',
  styleUrl: './notification-form.component.scss',
  standalone: false
})
export class NotificationFormComponent implements OnChanges {
  @Input() notification: Models.Notification;
  @Input() packageId: string;
  @Input() pak: Models.Package;
  @Output() saved = new EventEmitter<any>();
  @Output() close = new EventEmitter<any>();

  public notificationId: string;
  public groups: Models.Group[];
  public activeGroups: Models.Group[];
  public ghrio: Models.GroupHasRoleInOrganisation[];
  public headerButtons: any[];
  public contentTitle: string;
  public errors = [];
  public dynamicContent: Models.DynamicContent;
  public app: any;
  public today = new Date();
  private selectContentOpening: number;

  groupColumns = [{
    prop: 'name', translationString: 'userAdmin.frontend-group.name',
    valueTransformer: (val) => (val || '').startsWith('pref_') ? val.split('_').splice(2, val.split('_').length - 1).join('_') : val
  }, { prop: 'GroupID' }];

  constructor(
    private entranceService: DynamicContentEntranceService, private dialog: MatDialog, private groupService: GroupService, private securedAppService: GroupHasSecuredAppService,
    private ms: MessengerService, private ghrioService: GroupHasRoleInOrganisationService, private contentService: DynamicContentService, private ts: TranslateService,
    private notificationService: NotificationsService, private appService: PackageInstalledAppsService, private dialogService: DialogService, private http: HttpClient
  ) {
  }

  async ngOnChanges() {
    delete this.contentTitle;
    this.errors = [];
    this.notificationId = this.notification._id;
    if (this.notification?.contentId) {
      await this.setContent(this.notification.contentId);
    }

    const isUserManager = this.ms.$activePrivileges.getValue().find(p => p.value === 'my-users');

    if (isUserManager) {
      this.ghrio = (await this.ghrioService.find(0, 1000, { OrganisationID: this.ms.getOrganisation().OrganisationID, GroupID: { $ne: 8 } })).data;
      this.groups = (await this.groupService.find(0, this.ghrio.length, {
        GroupID: { $in: this.ghrio.filter(el => [45, 49, 53, 55].includes(parseInt(el.RoleID, 10))).map(el => el.GroupID) }
      })).data;
    }

    this.activeGroups = this.groups.filter(g => (this.notification?.groupIds || []).includes(g.GroupID.toString()));
    this.updateHeaderButtons();
  }

  async openTimePicker(time: string, type: string) {
    let dialogRef: MatDialogRef<TimePickerComponent>;

    if (time === undefined) {
      time = null;
    }

    dialogRef = this.dialog.open(TimePickerComponent, { width: '398px', height: '548px', panelClass: 'time', data: time });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }
    const hour = String(result.hour % 24).padStart(2, '0');
    const minute = String(result.minute).padStart(2, '0');
    this.notification.publishStartTime = hour + ':' + minute;
  }

  async removeGroup(event: any) {
    const groups = JSON.parse(JSON.stringify(this.activeGroups));
    groups.splice(event.index, 1);
    this.activeGroups = null;
    setTimeout(() => {
      this.activeGroups = groups;
      this.notification.groupIds = this.activeGroups.map(g => g.GroupID.toString());
      this.updateHeaderButtons();
    }, 0);
  }

  async addGroup() {
    const active = this.activeGroups;
    let all = this.groups;
    if (this.app?.isSecured && this.notification.contentId) {
      const securedAppGroups = (await this.securedAppService.find(0, 100, { packageInstalledAppId: this.app._id })).data;
      all = JSON.parse(JSON.stringify(all)).filter(g => securedAppGroups.find(sg => sg.Groups_GroupID === g.GroupID));
    }
    const groupIds = all.filter(el => !active.includes(el)).map(el => el.GroupID);
    let items = (await this.groupService.find(0, 1000, { GroupID: { $in: groupIds } })).data;
    items = items.map(el => {
      if ((el?.name || '').startsWith('pref_')) {
        el.name = el.name.split('_').splice(2, el.name.split('_').length - 1).join('_');
      }
      return el;
    });
    const dialogRef = this.dialog.open(AddItemsFromList, { width: '1200px', data: { availableItems: items, columns: [{ prop: 'name' }], idField: 'GroupID' } });
    const result = await dialogRef.afterClosed().toPromise();

    this.activeGroups = null;
    result?.additionalItems?.forEach(id => active.push(all.find(el => el.GroupID === id)));
    setTimeout(() => {
      this.activeGroups = active;
      this.notification.groupIds = this.activeGroups.map(g => g.GroupID.toString());
      this.updateHeaderButtons();
    }, 0);
  }

  updateHeaderButtons() {
    this.headerButtons = [{ icon: 'add', callback: () => {return this.addGroup()}, disabled: this.activeGroups.length === this.groups.length }];
  }

  async selectContent(event: any) {
    if (this.selectContentOpening && (Date.now() - this.selectContentOpening) <= 500) {
      return;
    }
    this.selectContentOpening = Date.now();
    if (event.type === 'focusin') {
      event.srcElement.blur();
    }
    event?.stopPropagation?.();
    event?.preventDefault?.();
    const res = await this.dialog.open(ContentSelectionComponent, { width: '1200px', data: { packageId: this.packageId } }).afterClosed().toPromise();
    if (!res) {
      return;
    }
    const appCopy = JSON.parse(JSON.stringify(this.app || {}));
    await this.setContent(res);
    if (this.app !== appCopy) {
      this.notification.groupIds = [];
      this.activeGroups = [];
    }
  }

  removeContent(event: any) {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    this.contentTitle = null;
    this.notification.groupIds = [];
    this.activeGroups = [];
    return this.notification.contentId = null;
  }

  async setContent(id: string) {
    this.dynamicContent = await this.contentService.get(id);
    const parentChain = [this.dynamicContent];
    let parent = (await this.contentService.find(0, 1, { children: parentChain[0]._id }))?.data?.[0];
    while (parent) {
      parentChain.unshift(parent);
      parent = (await this.contentService.find(0, 1, { children: parentChain[0]._id }))?.data?.[0];
    }
    const entrance = (await this.entranceService.find(0, 1, { children: parentChain[0]._id }))?.data?.[0];
    this.app = (await this.appService.find(0, 1, { dynamicContentEntranceId: entrance?._id }))?.data?.[0];
    this.contentTitle = [this.app?.translations?.[0]?.appTitle].concat(parentChain.map(p => p.title)).join('/');
    this.notification.contentId = id;
  }

  async abort() {
    const confirmation = await this.dialogService.confirm('system.general.pendingChanges');
    if (confirmation) {
      this.close.emit();
    }
  }

  async save() {
    this.errors = await this.verify();
    if (this.errors.length) return;
    let res: any;
    if (!this.notification.groupIds?.length) {

    }
    if (!!this.notification.instant || !this.notification.groupIds?.length) {
      let messageKey = 'notifications.reallySendInstant';
      if (this.pak?.ibexApp?.notificationsOnlyWithGroup && !this.notification.groupIds?.length) {
        return this.dialogService.confirm('notifications.cantSendWithoutGroup');
      } else if (!this.pak?.ibexApp?.notificationsOnlyForRegistered && !this.notification.groupIds?.length) {
        messageKey = `notifications.reallySend${!!this.notification.instant ? 'Instant' : ''}Groups`
      } else if (this.pak?.ibexApp?.notificationsOnlyForRegistered && !this.notification.groupIds?.length) {
        messageKey = `notifications.reallySend${!!this.notification.instant ? 'Instant' : ''}RegisteredGroups`
      }
      res = await this.dialogService.confirm(messageKey);
      if (!res) {
        return;
      }
    }
    if (this.notificationId) {
      this.notification = await this.notificationService.update(this.notificationId, this.notification);
    } else {
      this.notification = await this.notificationService.create(this.notification);
    }
    if (this.notification.instant) {
      const options = { headers: { 'authentication': this.ms.basicMessengerService.getAuthenticationHeader() } };
      await this.http.post(environment.apiBaseURL + '/push/notification', { id: this.notification._id }, options).toPromise();
      this.saved.emit();
      this.close.emit();
    } else {
      this.saved.emit();
    }
  }

  async verify() {
    let errors = [];
    if (this.notification.contentId) {
      let date = new Date();
      if (!this.notification.instant) {
        date = new Date(this.notification.publishStart);
        if (this.notification.publishStartTime) {
          date.setHours(parseInt(this.notification.publishStartTime.split(':')[0]));
          date.setMinutes(parseInt(this.notification.publishStartTime.split(':')[1]));
        }
      }
      const state = new ActiveStateService(this.app).check(this.dynamicContent, date);
      if (state !== 'active') {
        errors.push('content');
      }
      if (this.app?.isSecured && !this.notification.groupIds?.length)  {
        errors.push('groups');
      }
    }
    errors = errors.concat((this.notification.title?.length || 0) < 3 ? ['title'] : []);
    errors = errors.concat((this.notification.text?.length || 0) < 3 ? ['text'] : []);
    errors = errors.concat(!this.notification.instant && !this.notification.publishStart ? ['publishing'] : []);
    return errors;
  }

  getSendState(notification: any) {
    if (!notification.sent && notification.instant) {
      return 'sofort';
    }
    let d: any = new Date(notification.sent ? notification.sent : notification.publishStart);
    const options: any = { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' };
    const format = new Intl.DateTimeFormat('de-DE', notification.sent ? { ...options, hour: '2-digit', minute: '2-digit', hour12: false } : options);
    return (format.format(d).replace(/,/, '').replace(/\s*Uhr\s*$/i, '') + `${notification.sent ? '' : ' ' + notification.publishStartTime}`).replace('.', ',');
  }
}
